// GithubButton.tsx
import React, { useState, useRef, FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Button from '../../../../ui/components/Button';
import Tooltip from '../../../../ui/components/Tooltip';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import GithubMenu from './GithubMenu';
import { GithubFormat } from '../../utils/formatCode';
import { useUser } from '../../../../backend/useUser';
import { connectGithubAccount, getGithubAccountByUserId } from '../../../../backend/useGithubAccount';
import { createGithubRepo, getGithubRepo, GithubSyncStatus, checkGithubSyncStatus } from '../../../../backend/useGithubRepo';
import { syncDBCodeToGithub } from '../../../../backend/useGithubCode';
import { useCreditsContext } from '../../../pricing/CreditsContext';

const Root = styled.div`
    display: inline-block;
`;

export interface GithubData {
    user_name: string;
    repo_name: string;
    repo_link: string;
    status: GithubSyncStatus;
}

export enum GithubState {
    Loading = 'loading',
    ConnectAccount = 'connectAccount',
    CreateRepo = 'createRepo',
    SyncRepo = 'syncRepo',
}

interface GithubButtonProps {
    config: any;
    gameName: string;
    gameId: string;
    historyId: string;
    localCode: GithubFormat;
    prompt: string;
    historyStatus: string;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
}

const GithubButton: FunctionComponent<GithubButtonProps> = ({
    config,
    gameName,
    gameId,
    historyId,
    localCode,
    prompt,
    historyStatus,
    isGeneratingGame,
    isUpdatingGameArt,
}) => {
    const [open, setOpen] = useState(false);
    const [githubState, setGithubState] = useState<GithubState>(GithubState.Loading);
    const [githubData, setGithubData] = useState<GithubData>({
        user_name: '',
        repo_name: '',
        repo_link: undefined,
        status: undefined,
    });
    const anchorRef = useRef<HTMLDivElement>(null);
    const { user } = useUser();
    const { subscription } = useCreditsContext();
    const [status, setStatus] = useState<GithubSyncStatus>('pending');
    
    const updateGithubState = async () => {
        if (user?.id) {
            try {
                const githubAccount = await getGithubAccountByUserId(user.id);
                if (githubAccount) { // If connected GitHub account
                    setGithubData(prevData => ({
                        ...prevData,
                        user_name: githubAccount.github_username,
                    }));

                    const githubRepo = await getGithubRepo(user.id, gameId);
                    if (githubRepo) { // If repo exists
                        setGithubData(prevData => ({
                            ...prevData,
                            repo_name: githubRepo.repo_name,
                            repo_link: `https://github.com/${githubAccount.github_username}/${githubRepo.repo_name}`,
                            status: githubRepo.status as GithubSyncStatus,
                        }));
                        // If last state was error, allow retry by going to Create state
                        if ((githubRepo.status as GithubSyncStatus) === 'error') {
                            setGithubState(GithubState.CreateRepo);
                        } else {
                            setGithubState(GithubState.SyncRepo);
                            checkAndSetStatus();
                        }
                    } else { // If account exists but no repo
                        const cleanedRepoName = gameName.toLowerCase().replace(/\s+/g, '-');
                        setGithubData(prevData => ({
                            ...prevData,
                            repo_name: cleanedRepoName,
                        }));
                        setGithubState(GithubState.CreateRepo);
                    }
                } else { // No GitHub account connected
                    setGithubState(GithubState.ConnectAccount);
                }
            } catch (error) {
                console.error('Error checking GitHub account or repo status:', error);
                setGithubState(GithubState.ConnectAccount); // Fallback to connect if any error
            }
        } else {
            setGithubState(GithubState.ConnectAccount); // User not logged in
        }
    };
    
    const handleButtonClick = () => {
        setOpen((prevOpen) => !prevOpen);
        if (!open) {
            updateGithubState();
        }
    };

    const handleClickAway = () => {
        setOpen(false);
    };

    const checkAndSetStatus = async (pollSeconds: number = 0) => {
        if (!user?.id) {
            // console.error('User ID not available to check sync status.');
            return;
        }
        if (!gameId) {
            // console.error('Game ID not available to check sync status.');
            return;
        }

        try {
            const currentStatus = await checkGithubSyncStatus(user.id, gameId, pollSeconds);
            setStatus(currentStatus);
        } catch (error) {
            console.error('Error fetching sync status:', error);
            setStatus('error'); // Set status to error on failure
        }
    };

    // Whenever the user submits a new prompt, check status
    useEffect(() => {
        if (!user?.id) return;
        if (subscription === 'free' || subscription === null || subscription === undefined) return;

        if (isGeneratingGame === true || isUpdatingGameArt === true) {
            checkAndSetStatus(30);
        }
    }, [isGeneratingGame, isUpdatingGameArt, subscription]);

    // Whenever the latest user facing history entry changes, check status
    useEffect(() => {
        if (subscription === 'free' || subscription === null || subscription === undefined) return;
        if (historyId) {
            checkAndSetStatus(10);
        }
    }, [historyId, subscription]);
    
    // Polling effect: Poll for status if it's 'syncing' or 'pending' and the menu is open
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
    
        if ((status === 'syncing' || status === 'pending') && open) {
            interval = setInterval(() => {
                checkAndSetStatus();
            }, 5000); // Poll every 5 seconds
        }
    
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [status, open]);
    
    const handleConnectAccount = async () => {
        // console.log('Connect account clicked');
        if (!user?.id) {
            console.error('User not logged in.');
            return;
        }

        try {
            // Call the function which now handles opening the window
            connectGithubAccount(user.id);

            const messageHandler = async (event: MessageEvent) => {
                // Ensure the message is from the correct origin and contains the expected data
                if (event.origin === window.location.origin && event.data === 'github_oauth_success') {
                    // console.log('Received success message from new window.');
                    window.removeEventListener('message', messageHandler); // Clean up the listener

                    const connectedAccount = await getGithubAccountByUserId(user.id);
                    if (connectedAccount) {
                        // If a repo exists, update the state to sync it
                        const githubRepo = await getGithubRepo(user.id, gameId);
                        if (githubRepo) {
                            setGithubData(prevData => ({
                                ...prevData,
                                user_name: connectedAccount.github_username,
                                repo_name: githubRepo.repo_name,
                                repo_link: `https://github.com/${connectedAccount.github_username}/${githubRepo.repo_name}`,
                                status: githubRepo.status as GithubSyncStatus,
                            }));
                            setGithubState(GithubState.SyncRepo);
                            checkAndSetStatus();
                        } else {
                            // If no repo exists, proceed to create a new one
                            const cleanedRepoName = gameName.toLowerCase().replace(/\s+/g, '-');
                            setGithubData((prevData) => ({
                                ...prevData,
                                user_name: connectedAccount.github_username,
                                repo_name: cleanedRepoName,
                            }));
                            setGithubState(GithubState.CreateRepo);
                        }
                    } else {
                        console.warn('GitHub account not found after connection attempts.');
                        setGithubState(GithubState.ConnectAccount);
                    }
                }
            };
            window.addEventListener('message', messageHandler);

        } catch (error) {
            console.error('Failed to initiate GitHub connection or check repo status:', error);
            // TODO: user facing error
        }
    };

    const handleCreateRepo = async () => {
        // console.log('Create repo clicked');
        if (!user?.id) {
            console.error('User not logged in.');
            return;
        }

        if (!gameId) {
            console.error('Game ID not available for repository creation.');
            // Handle this error appropriately, perhaps show a user-facing message
            return;
        }

        try {
            // Determine if repo already exists
            const existingRepo = await getGithubRepo(user.id, gameId);

            // Enter syncing state immediately for better UX
            setStatus('syncing');
            setGithubState(GithubState.SyncRepo);

            if (existingRepo) {
                // Retry path: push current DB code to GitHub
                const result = await syncDBCodeToGithub(user.id, gameId);
                if (result.status === 'ERROR') {
                    setStatus('error');
                } else {
                    checkAndSetStatus(10);
                }
            } else {
                // Create path: create repo then push
                const createdRepo = await createGithubRepo(
                    user.id,
                    gameId,
                    githubData.repo_name,
                    'pending'
                );

                if (createdRepo?.repo_name) {
                    setGithubData((prevData) => ({
                        ...prevData,
                        repo_name: createdRepo.repo_name,
                        repo_link: `https://github.com/${githubData.user_name}/${createdRepo.repo_name}`,
                        status: createdRepo.status as GithubSyncStatus,
                    }));
                }

                const result = await syncDBCodeToGithub(user.id, gameId);
                if (result.status === 'ERROR') {
                    setStatus('error');
                } else {
                    checkAndSetStatus(10);
                }
            }
        } catch (error) {
            console.error('Failed to create or sync GitHub repository:', error);
            setGithubState(GithubState.CreateRepo);
            setStatus('error');
        }
    };

    // If user is not a paid subscriber, hide github button
    if (subscription === 'free' || subscription === null || subscription === undefined) {
        return null;
    }

    // If localCode is a single file, hide the github button
    const isMultifile = localCode?.length > 1;
    if (!isMultifile) {
        return null;
    }

    return (
        <Root ref={anchorRef}>
            <Tooltip text='Github Sync' colorMode="dark" hide={open}>
                <Button
                    size="tiny"
                    color="ghost white"
                    icon={'github-logo'}
                    onClick={handleButtonClick}
                />
            </Tooltip>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom"
                sx={{ padding: '10px', zIndex: 1500 }}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <GithubMenu
                        githubState={githubState}
                        status={status}
                        onConnectClick={handleConnectAccount}
                        onCreateRepoClick={handleCreateRepo}
                        githubData={githubData}
                        retryMode={githubState === GithubState.CreateRepo && (githubData.status === 'error')}
                    />
                </ClickAwayListener>
            </Popper>
        </Root>
    );
};

export default GithubButton;